/**
 * The board over HTTP — `BoardStore` against the server's `/api/board`.
 *
 * The session token comes from Clerk on the React side and is handed in as a
 * function, so this stays a plain class the tests can drive with a fake fetch.
 */

import { deserialize, serialize, type PlayDocument } from '../core/index.ts';
import type { BoardStore } from './types.ts';

export class HttpBoardStore implements BoardStore {
  readonly #getToken: () => Promise<string | null>;
  readonly #fetch: typeof fetch;

  constructor(getToken: () => Promise<string | null>, fetchImpl: typeof fetch = (...args) => fetch(...args)) {
    this.#getToken = getToken;
    this.#fetch = fetchImpl;
  }

  async load(): Promise<PlayDocument | undefined> {
    const response = await this.#request('GET');
    if (response.status === 404) return undefined;
    if (!response.ok) throw new Error(`Could not load the board (${response.status})`);
    return deserialize(await response.text());
  }

  async save(doc: PlayDocument): Promise<void> {
    const response = await this.#request('PUT', serialize(doc));
    if (!response.ok) throw new Error(`Could not save the board (${response.status})`);
  }

  async #request(method: 'GET' | 'PUT', body?: string): Promise<Response> {
    const token = await this.#getToken();
    if (!token) throw new Error('Not signed in');
    return this.#fetch('/api/board', {
      method,
      headers: {
        authorization: `Bearer ${token}`,
        ...(body !== undefined ? { 'content-type': 'application/json' } : {}),
      },
      ...(body !== undefined ? { body } : {}),
    });
  }
}

/** For tests and for anything that must not hit the network. */
export class MemoryBoardStore implements BoardStore {
  doc: PlayDocument | undefined;

  async load(): Promise<PlayDocument | undefined> {
    return this.doc;
  }

  async save(doc: PlayDocument): Promise<void> {
    this.doc = doc;
  }
}
